import { ColDef } from 'ag-grid-community';
import { SysCodeDto } from 'src/types/base';
import { CommonCodeColumn, getCommonCodeColumnOptions } from 'src/utils/grid/GridColumnContent';
import { formatNumber } from 'src/utils/number/numberUtils';

const locationNmColumn: ColDef = {
    headerName: '보관위치',
    colId: 'locationNm',
    field: 'locationNm',
    width: 120,
    editable: false,
};

// 품목별 재고 컬럼 정의 (ITEM view)
export const getItemStockColumns = (unitList: SysCodeDto[]): ColDef[] => [
    { 
        headerName: '품목코드', 
        colId: 'itemCd', 
        field: 'itemCd', 
        width: 120, 
        editable: false,
        cellRenderer: (params: any) => {
            if (params.data?.isSubtotal) {
                return params.data.groupLabel || `${params.data.itemCd} 소계`;
            }
            return params.value || '';
        }
    },
    { headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 220, editable: false },
    { headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150, editable: false },
	locationNmColumn,
    { headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 80, ...CommonCodeColumn.options(unitList, false, undefined, undefined), },
    { headerName: '브랜드코드', colId: 'brandCd', field: 'brandCd', width: 110, editable: false },
    { headerName: '브랜드명', colId: 'brandNm', field: 'brandNm', width: 120, editable: false },
    { headerName: '등급', colId: 'itemStatus', field: 'itemStatus', width: 80, editable: false },
    { 
        headerName: '재고량', 
        colId: 'stockQty', 
        field: 'stockQty', 
        width: 100, 
        type: 'numericColumn', 
        comparator: (valueA, valueB) => valueA - valueB,
        valueFormatter: (p: any) => formatNumber(p.value || 0),
        cellRenderer: (params: any) => {
            if (params.data?.isSubtotal) {
                return formatNumber(params.data.stockQty || 0);
            }
            return params.valueFormatted ?? (params.value ?? '');
        },
        editable: false 
    },
    { 
        headerName: '출고예약', 
        colId: 'reservQty', 
        field: 'reservQty', 
        width: 100, 
        type: 'numericColumn', 
        comparator: (valueA, valueB) => valueA - valueB,
        valueFormatter: (p: any) => formatNumber(p.value || 0),
        cellRenderer: (params: any) => {
            if (params.data?.isSubtotal) {
                return formatNumber(params.data.reservQty || 0);
            }
            return params.valueFormatted ?? (params.value ?? '');
        },
        editable: false 
    },
    { 
        headerName: '가용재고', 
        colId: 'availableQty', 
        field: 'stockQty', 
        width: 100, 
        type: 'numericColumn', 
        comparator: (valueA, valueB) => valueA - valueB,
        valueFormatter: (p: any) => formatNumber(p.value || 0),
        editable: false 
    },
];

// 창고별 재고 컬럼 정의 (WAREHOUSE view)
export const getWarehouseStockColumns = (unitList: SysCodeDto[]): ColDef[] => [
    { headerName: '물류센터명', colId: 'logicCenterNm', field: 'logicCenterNm', width: 150, editable: false },
    { headerName: '물류센터', colId: 'logicCenter', field: 'logicCenter', width: 120, editable: false },
    { headerName: '창고코드', colId: 'warehouseCd', field: 'warehouseCd', width: 120, editable: false },
    { headerName: '창고명', colId: 'warehouseNm', field: 'warehouseNm', width: 150, editable: false },
    { 
        headerName: '품목코드', 
        colId: 'itemCd', 
        field: 'itemCd', 
        width: 120, 
        editable: false,
        cellRenderer: (params: any) => {
            if (params.data?.isSubtotal) {
                return params.data.groupLabel || `${params.data.itemCd} 소계`;
            }
            return params.value || '';
        }
    },
    { headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 200, editable: false },
    { headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150, editable: false },
	locationNmColumn,
    { headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 80, ...CommonCodeColumn.options(unitList, false, undefined, undefined), },
    { headerName: '등급', colId: 'itemStatus', field: 'itemStatus', width: 80, editable: false },
    { 
        headerName: '재고량', 
        colId: 'stockQty', 
        field: 'stockQty', 
        width: 100, 
        type: 'numericColumn', 
        comparator: (valueA, valueB) => valueA - valueB,
        valueFormatter: (p: any) => formatNumber(p.value || 0),
        cellRenderer: (params: any) => {
            if (params.data?.isSubtotal) {
                return formatNumber(params.data.stockQty || 0);
            }
            return params.valueFormatted ?? (params.value ?? '');
        },
        editable: false 
    },
    { 
        headerName: '출고예약', 
        colId: 'reservQty', 
        field: 'reservQty', 
        width: 100, 
        type: 'numericColumn', 
        comparator: (valueA, valueB) => valueA - valueB,
        valueFormatter: (p: any) => formatNumber(p.value || 0),
        cellRenderer: (params: any) => {
            if (params.data?.isSubtotal) {
                return formatNumber(params.data.reservQty || 0);
            }
            return params.valueFormatted ?? (params.value ?? '');
        },
        editable: false 
    },
    { 
        headerName: '가용재고', 
        colId: 'availableQty', 
        field: 'stockQty', 
        width: 100, 
        type: 'numericColumn', 
        comparator: (valueA, valueB) => valueA - valueB,
        valueFormatter: (p: any) => formatNumber(p.value || 0),
        editable: false 
    },
];

// 브랜브별 컬럼 정의 (Brand view)
export const getBrandStockColumns = (unitList: SysCodeDto[]): ColDef[] => [
    { headerName: '브랜드코드', colId: 'brandCd', field: 'brandCd', width: 110, editable: false },
    { headerName: '브랜드명', colId: 'brandNm', field: 'brandNm', width: 120, editable: false },
    { headerName: '물류센터명', colId: 'logicCenterNm', field: 'logicCenterNm', width: 150, editable: false },
    { headerName: '물류센터', colId: 'logicCenter', field: 'logicCenter', width: 120, editable: false },
    { headerName: '창고코드', colId: 'warehouseCd', field: 'warehouseCd', width: 120, editable: false },
    { headerName: '창고명', colId: 'warehouseNm', field: 'warehouseNm', width: 120, editable: false },
    { 
        headerName: '품목코드', 
        colId: 'itemCd', 
        field: 'itemCd', 
        width: 120, 
        editable: false,
        cellRenderer: (params: any) => {
            if (params.data?.isSubtotal) {
                return params.data.groupLabel || `${params.data.itemCd} 소계`;
            }
            return params.value || '';
        }
    },
    { headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 200, editable: false },
    { headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 150, editable: false },
	locationNmColumn,
    { headerName: '등급', colId: 'itemStatus', field: 'itemStatus', width: 80, editable: false },
    { headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 80, ...CommonCodeColumn.options(unitList, false, undefined, undefined), },
    { 
        headerName: '재고량', 
        colId: 'stockQty', 
        field: 'stockQty', 
        width: 100, 
        type: 'numericColumn', 
        comparator: (valueA, valueB) => valueA - valueB,
        valueFormatter: (p: any) => formatNumber(p.value || 0),
        cellRenderer: (params: any) => {
            if (params.data?.isSubtotal) {
                return formatNumber(params.data.stockQty || 0);
            }
            return params.valueFormatted ?? (params.value ?? '');
        },
        editable: false 
    },
    { 
        headerName: '출고예약', 
        colId: 'reservQty', 
        field: 'reservQty', 
        width: 100, 
        type: 'numericColumn', 
        comparator: (valueA, valueB) => valueA - valueB,
        valueFormatter: (p: any) => formatNumber(p.value || 0),
        cellRenderer: (params: any) => {
            if (params.data?.isSubtotal) {
                return formatNumber(params.data.reservQty || 0);
            }
            return params.valueFormatted ?? (params.value ?? '');
        },
        editable: false 
    },
    { 
        headerName: '가용재고', 
        colId: 'availableQty', 
        field: 'stockQty', 
        width: 100, 
        type: 'numericColumn', 
        comparator: (valueA, valueB) => valueA - valueB,
        valueFormatter: (p: any) => formatNumber(p.value || 0),
        editable: false 
    },
];


// 탭별 그리드 옵션 - 최종 옵션으로 통일
export const getTabGridOptions = () => ({
    // DOM 순서 보장: 행의 DOM 순서가 데이터 순서와 일치하도록 보장
    // 가상화된 그리드에서 스크롤 시 행 순서가 바뀌는 것을 방지
    ensureDomOrder: true,
    
    // 행 호버 하이라이트 비활성화: 마우스 오버 시 행 하이라이트 효과 제거
    // 사용자 경험 개선을 위해 선택된 행과 구분하기 위함
    suppressRowHoverHighlight: true,
    
    // 셀 포커스 비활성화: 셀 클릭 시 포커스 표시 제거 (현재 주석 처리됨)
    // suppressCellFocus: true, << 잠그지 말것
    
    // 페이지네이션 비활성화: 모든 데이터를 한 번에 표시
    // 대용량 데이터의 경우 성능에 영향을 줄 수 있음
    pagination: false,
    
    // 캐시 블록 최대 개수: 가상화된 그리드에서 메모리에 유지할 행 블록 수
    // 메모리 사용량과 렌더링 성능의 균형점
    maxBlocksInCache: 10,
    
    // 블록 로드 디바운스 시간: 스크롤 시 새로운 블록 로드 지연 시간 (ms)
    // 빠른 스크롤 시 불필요한 렌더링을 방지하여 성능 최적화
    blockLoadDebounceMillis: 100,
    
    // 브라우저 리사이즈 옵저버 비활성화: 창 크기 변경 감지 비활성화
    // 그리드 크기 조정 시 불필요한 리렌더링 방지
    suppressBrowserResizeObserver: true,
    
    // 속성명 검사 비활성화: 컬럼 정의의 속성명 유효성 검사 비활성화
    // 개발 모드에서 경고 메시지 억제
    suppressPropertyNamesCheck: true,
    
    // 수직 스크롤바 디바운스: 스크롤 이벤트 처리 지연
    // 스크롤 성능 최적화를 위한 디바운싱 적용
    debounceVerticalScrollbar: true,
    
    // 수평 스크롤바 디바운스: 수평 스크롤 이벤트 처리 지연
    // 스크롤 성능 최적화를 위한 디바운싱 적용
    debounceHorizontalScrollbar: true,
    
    // 셀 변경 플래시 비활성화: 셀 값 변경 시 깜빡임 효과 제거
    // 사용자 경험 개선 및 시각적 방해 요소 제거
    suppressCellChangeFlash: true,
});
